import { Directive, ElementRef, HostListener, Input, Renderer2, OnDestroy } from '@angular/core';
import { TooltipService } from '../services/tooltip.service';

@Directive({
    selector: '[customTooltip]',
    standalone: false
})
export class CustomTooltipDirective implements OnDestroy {
  @Input('customTooltip') tooltipText: string = '';
  @Input() tooltipClass: string = 'custom-tooltip';

  private tooltipElement: HTMLElement | null = null;
  private hideTimeout: any;
  private showTimeout: any;
  private cleanupFn: () => void;

  constructor(
    private el: ElementRef, 
    private renderer: Renderer2,
    private tooltipService: TooltipService
  ) {
    // Register cleanup function with the service
    this.cleanupFn = () => this.hideTooltip();
    this.tooltipService.registerTooltip(this.cleanupFn);
  }

  @HostListener('mouseenter') onMouseEnter() {
    // Clear any existing timeouts
    this.clearTimeouts();
    
    // Show tooltip with slight delay
    this.showTimeout = setTimeout(() => {
      this.showTooltip();
    }, 200);
  }

  @HostListener('mouseleave') onMouseLeave() {
    // Clear show timeout if mouse leaves quickly
    this.clearTimeouts();
    // Hide immediately on mouse leave
    this.hideTooltip();
  }

  @HostListener('focus') onFocus() {
    this.showTooltip();
  }

  @HostListener('blur') onBlur() {
    this.hideTooltip();
  }

  // Handle touch events for mobile
  @HostListener('touchstart') onTouchStart() {
    // Clear any existing timeouts
    this.clearTimeouts();
    
    // Show tooltip immediately on mobile
    this.showTooltip();

  }

  @HostListener('touchend') onTouchEnd() {
    // Do nothing - let the timeout handle hiding
  }

  @HostListener('click') onClick() {
    // Hide tooltip immediately when element is clicked
    this.hideTooltip();
  }

  ngOnDestroy() {
    // Clean up tooltip and timeout when directive is destroyed
    this.hideTooltip();
    this.clearTimeouts();
    // Unregister from the service
    this.tooltipService.unregisterTooltip(this.cleanupFn);
  }

  private clearTimeouts() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private showTooltip() {
    if (this.tooltipElement || !this.tooltipText) {
      return;
    }

    // Set this tooltip as active and hide others
    this.tooltipService.setActiveTooltip(this.cleanupFn);

    // Create tooltip element
    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'absolute');
    this.renderer.addClass(this.tooltipElement, 'z-50');
    this.renderer.addClass(this.tooltipElement, 'px-2');
    this.renderer.addClass(this.tooltipElement, 'py-1');
    this.renderer.addClass(this.tooltipElement, 'text-sm');
    this.renderer.addClass(this.tooltipElement, 'text-white');
    this.renderer.addClass(this.tooltipElement, 'bg-black');
    this.renderer.addClass(this.tooltipElement, 'bg-opacity-80');
    this.renderer.addClass(this.tooltipElement, 'rounded');
    this.renderer.addClass(this.tooltipElement, 'shadow-lg');
    this.renderer.addClass(this.tooltipElement, 'whitespace-nowrap');
    this.renderer.addClass(this.tooltipElement, 'pointer-events-none');
    this.renderer.addClass(this.tooltipElement, 'transition-opacity');
    this.renderer.addClass(this.tooltipElement, 'duration-200');

    // Add custom class if provided
    if (this.tooltipClass) {
      this.renderer.addClass(this.tooltipElement, this.tooltipClass);
    }

    // Set tooltip text
    const textNode = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(this.tooltipElement, textNode);

    // Add to DOM
    this.renderer.appendChild(document.body, this.tooltipElement);

    // Position tooltip after it's in the DOM
    setTimeout(() => {
      this.positionTooltip();
    }, 0);
  }

  private hideTooltip() {
    if (this.tooltipElement) {
      try {
        // Check if element is still in DOM before removing
        if (this.tooltipElement.parentNode) {
          this.renderer.removeChild(this.tooltipElement.parentNode, this.tooltipElement);
        }
      } catch (error) {
        // Silently handle any DOM errors
        console.debug('Tooltip cleanup error:', error);
      }
      this.tooltipElement = null;
      
      // Clear this tooltip as active
      this.tooltipService.clearActiveTooltip(this.cleanupFn);
    }
    
    // Clear any pending timeouts
    this.clearTimeouts();
  }

  private positionTooltip() {
    if (!this.tooltipElement) return;

    const hostElement = this.el.nativeElement;
    const hostRect = hostElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    // Default position: above the element
    let top = hostRect.top - tooltipRect.height - 8;
    let left = hostRect.left + (hostRect.width / 2) - (tooltipRect.width / 2);

    // Check if tooltip goes off-screen and adjust
    if (top < 0) {
      // Position below if no space above
      top = hostRect.bottom + 8;
    }

    if (left < 0) {
      left = 8;
    } else if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 8;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left + window.scrollX}px`);
  }
}
